/**
 * Update tracking number — what the seller posts to
 * `POST /api/orders/update-tracking` to attach a carrier tracking number to
 * a `seller_orders` row. Server auto-flips the order to `shipped` on first
 * tracking (idempotent across concurrent calls via conditional UPDATE) and
 * emits a buyer email + a PayPal dispute-evidence submission.
 *
 * The carrier is hardcoded to `OTHER` server-side; the client never sends a
 * carrier name. `trackingNumber` is capped to 100 chars so the email template
 * and dispute payload stay predictable.
 *
 * Mobile-consumed (api-orders.ts updateOrderTracking). Schema is additive —
 * only the two fields mobile already sends.
 *
 * @see app/api/orders/update-tracking/route.ts — handler
 * @see apps/mobile/lib/api-orders.ts updateOrderTracking — mobile caller
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md H-3
 */

import { z } from "zod";
import { pinned } from "./pin";

export const TRACKING_NUMBER_MAX_LENGTH = 100;

export interface UpdateOrderTrackingRequest {
  /** `seller_orders.id` to attach tracking to. */
  sellerOrderId: string;
  /** Carrier tracking number. Non-empty, capped at 100 chars. */
  trackingNumber: string;
}

export const UpdateOrderTrackingRequestSchema = pinned<UpdateOrderTrackingRequest>()(
  z.object({
    sellerOrderId: z.string().min(1),
    trackingNumber: z.string().min(1).max(TRACKING_NUMBER_MAX_LENGTH),
  }),
);
